import React, { useState, useEffect, useCallback } from 'react';
import { ewayBillAPI } from '../../services/api';
import toast from 'react-hot-toast';

const fmt = (n) => Number(n||0).toLocaleString('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0});
const statusMap = { pending:'badge-yellow', generated:'badge-green', cancelled:'badge-red', failed:'badge-red', expired:'badge-gray' };

export default function EwayBillList() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genForm, setGenForm] = useState({
    invoice_id: '', transporter_name: '', transporter_id: '', transport_mode: 1,
    vehicle_number: '', distance_km: '', lr_number: '', lr_date: ''
  });
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await ewayBillAPI.list({ limit: 100 }); setBills(r.data.eway_bills); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (!genForm.invoice_id) return toast.error('Enter invoice ID');
    setGenerating(true);
    try {
      const res = await ewayBillAPI.generate(genForm);
      if (res.data.success) {
        toast.success(res.data.ewb_number ? `E-way Bill Generated: ${res.data.ewb_number}` : 'E-way bill data prepared (offline mode)');
        setShowGenerate(false);
        load();
      } else {
        toast.error(res.data.errors?.[0] || 'Generation failed');
      }
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setGenerating(false); }
  };

  const handleCancel = async (ewbNumber) => {
    if (!window.confirm(`Cancel E-way Bill ${ewbNumber}?`)) return;
    try {
      await ewayBillAPI.cancel({ ewb_number: ewbNumber, reason_code: 4, remarks: 'Cancelled by user' });
      toast.success('Cancelled');
      load();
    } catch { toast.error('Failed to cancel'); }
  };

  const handleDownload = async (id, docNo) => {
    try {
      const res = await ewayBillAPI.downloadJSON(id);
      const blob = new Blob([res.data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `EWB-${docNo.replace(/\//g,'-')}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('JSON downloaded — upload to ewaybill.nic.in');
    } catch { toast.error('Download failed'); }
  };

  const set = (f) => (e) => setGenForm(p => ({...p, [f]: e.target.value}));

  return (
    <div>
      <div className="page-header">
        <div><h1>E-way Bills</h1><p className="subtitle">Generate and manage e-way bills for goods transport</p></div>
        <button onClick={() => setShowGenerate(true)} className="btn btn-primary">+ Generate E-way Bill</button>
      </div>

      {/* Generate Modal */}
      {showGenerate && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowGenerate(false)}>
          <div className="modal modal-lg">
            <div className="modal-header"><h2>Generate E-way Bill</h2><button onClick={() => setShowGenerate(false)} className="btn btn-icon">✕</button></div>
            <div className="modal-body">
              <p className="text-sm text-secondary mb-4">E-way bill is mandatory for goods movement exceeding ₹50,000. Fill transport details below.</p>
              <div className="form-row form-row-3">
                <div className="form-group"><label className="form-label">Invoice ID *</label><input className="form-input" value={genForm.invoice_id} onChange={set('invoice_id')} placeholder="Paste invoice UUID from invoice view" /></div>
                <div className="form-group"><label className="form-label">Transporter Name</label><input className="form-input" value={genForm.transporter_name} onChange={set('transporter_name')} /></div>
                <div className="form-group"><label className="form-label">Transporter GSTIN</label><input className="form-input" value={genForm.transporter_id} onChange={set('transporter_id')} placeholder="15-char GSTIN" /></div>
              </div>
              <div className="form-row form-row-4">
                <div className="form-group"><label className="form-label">Transport Mode</label>
                  <select className="form-select" value={genForm.transport_mode} onChange={set('transport_mode')}>
                    <option value={1}>Road</option><option value={2}>Rail</option><option value={3}>Air</option><option value={4}>Ship</option>
                  </select></div>
                <div className="form-group"><label className="form-label">Vehicle Number</label><input className="form-input" value={genForm.vehicle_number} onChange={set('vehicle_number')} placeholder="e.g. UP32AB1234" /></div>
                <div className="form-group"><label className="form-label">Distance (km)</label><input type="number" className="form-input" value={genForm.distance_km} onChange={set('distance_km')} /></div>
                <div className="form-group"><label className="form-label">L.R. Number</label><input className="form-input" value={genForm.lr_number} onChange={set('lr_number')} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowGenerate(false)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleGenerate} className="btn btn-primary" disabled={generating}>{generating ? 'Generating...' : 'Generate E-way Bill'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-container">
          {loading ? <div className="loading-container"><div className="spinner" /></div> : (
            <table>
              <thead><tr><th>EWB No.</th><th>Invoice</th><th>Customer</th><th>Route</th><th>Vehicle</th><th style={{textAlign:'right'}}>Value</th><th>Status</th><th>Valid Upto</th><th>Actions</th></tr></thead>
              <tbody>
                {bills.map(b => (
                  <tr key={b.id}>
                    <td className="td-mono font-semibold">{b.ewb_number || '—'}</td>
                    <td className="td-mono">{b.doc_no}</td>
                    <td>{b.customer_name}</td>
                    <td className="text-secondary text-sm">{b.from_place} → {b.to_place}<br/>{b.distance_km > 0 ? `${b.distance_km} km` : ''}</td>
                    <td className="td-mono">{b.vehicle_number || '—'}</td>
                    <td className="td-amount">{fmt(b.total_inv_value)}</td>
                    <td><span className={`badge ${statusMap[b.ewb_status]}`}>{b.ewb_status}</span></td>
                    <td className="text-secondary text-sm">{b.valid_upto ? new Date(b.valid_upto).toLocaleDateString('en-IN') : '—'}</td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => handleDownload(b.id, b.doc_no)} className="btn btn-outline btn-sm" title="Download JSON for portal upload">⬇ JSON</button>
                        {b.ewb_status === 'generated' && <button onClick={() => handleCancel(b.ewb_number)} className="btn btn-danger btn-sm">Cancel</button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {bills.length === 0 && <tr><td colSpan={9} className="text-center text-secondary" style={{padding:40}}>No e-way bills yet. Generate one from an invoice.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card" style={{marginTop:16}}>
        <div className="card-body">
          <h3 style={{fontSize:14, fontWeight:600, marginBottom:8}}>How E-way Bills work in this system</h3>
          <p className="text-sm text-secondary" style={{lineHeight:1.7}}>
            When NIC API credentials are configured in <code>.env</code>, e-way bills are generated automatically via the government portal.
            Without API credentials, the system runs in <strong>offline mode</strong> — it prepares the complete JSON payload which you can download
            and manually upload to <a href="https://ewaybill.nic.in" target="_blank" rel="noopener noreferrer" style={{color:'var(--primary)'}}>ewaybill.nic.in</a>.
            E-way bills are mandatory for goods transport exceeding ₹50,000 in invoice value.
          </p>
        </div>
      </div>
    </div>
  );
}
